/**
 * 메뉴 사용 여부 게이트 — "이 화면/위젯이 가리키는 메뉴를 관리자가 꺼 놨는가?"
 *
 * 2026-08-11 사용자 요구: **메뉴 관리에서 사용 안 하는 위젯은 "메뉴 관리에서 사용하라"고
 * 위젯에 표기**한다. 그러려면 화면이 "꺼짐"과 "애초에 없는 메뉴"를 구분할 수 있어야 한다.
 *
 * 그래서 소스가 `/menu-config` 가 아니라 **`GET /api/v1/menus`** 다.
 *   · `/menu-config`(NavBar·StatusBar 용, [[lib/menu-config.ts]])는 **켜진 행만** 내려준다 →
 *     목록에 없는 키가 "꺼짐"인지 "미등록"인지 알 수 없다. 메뉴를 그리는 데는 충분하지만
 *     "꺼져 있으니 켜세요" 를 말하려면 부족하다.
 *   · `/api/v1/menus` 는 **모든 행 + visible 플래그 + registryPresent** 를 준다. visible 은
 *     레지스트리 enabled 와 회사별 서비스모듈·플랜 게이트를 이미 AND 한 값이라, 화면이
 *     서버의 차단 판정(chat-server menu-registry-guard)과 어긋나지 않는다.
 *
 * 판정 규칙(서버 가드와 동일하게 맞춘다):
 *   registryPresent=false        → 전부 사용 가능(fail-open)
 *   해당 menu_key 행 없음        → 사용 가능(미등록 메뉴까지 잠그지 않는다 — 전자결재 등)
 *   행 있음 + visible=false      → **사용 안 함** → 위젯이 안내 문구를 띄운다
 *
 * 조회 실패(네트워크·401 등)도 fail-open. 안내 문구 때문에 멀쩡한 위젯을 가리는 쪽이
 * 데이터가 잠깐 안 보이는 것보다 나쁘다.
 */

import { useSyncExternalStore } from 'react';
import { apiClient } from '../api/client';

interface MenuVisibilityRow {
  menuKey: string;
  label: string;
  moduleKey: string | null;
  section: string;
  sortOrder: number;
  visible: boolean;
}

interface GateState {
  /** null = 아직 모름/조회 실패/레지스트리 없음 → 전부 사용 가능. */
  rows: MenuVisibilityRow[] | null;
}

let state: GateState = { rows: null };
const subscribers = new Set<() => void>();
let started = false;

function setState(next: GateState): void {
  state = next;
  for (const fn of subscribers) fn();
}

/** `/api/v1/menus` 1회 조회 — 앱당 1요청(구독자가 몇이든). */
export function ensureMenuGateLoaded(): void {
  if (started) return;
  started = true;
  try {
    apiClient
      .get<{ registryPresent?: boolean; menus?: MenuVisibilityRow[] }>('/menus')
      .then(({ data }) => {
        if (!data?.registryPresent || !Array.isArray(data.menus)) {
          setState({ rows: null });
          return;
        }
        setState({ rows: data.menus });
      })
      .catch(() => setState({ rows: null }));
  } catch {
    setState({ rows: null });
  }
}

function subscribe(fn: () => void): () => void {
  subscribers.add(fn);
  ensureMenuGateLoaded();
  return () => { subscribers.delete(fn); };
}

function getSnapshot(): GateState {
  return state;
}

export interface MenuGate {
  /** false 면 관리자가 이 메뉴를 껐다 — 데이터를 조회하지 말고 안내를 띄운다. */
  enabled: boolean;
  /** 레지스트리에 등록된 메뉴 이름(안내 문구에 그대로 쓴다). 없으면 호출자가 준 기본값. */
  label: string;
}

/**
 * 메뉴 사용 여부 + 표시용 이름.
 * @param menuKey `admin_menu_registry.menu_key` (예: 'info' · 'mail' · 'calendar' · 'task' · 'chat')
 * @param fallbackLabel 레지스트리에 이름이 없을 때 쓸 기본 표기
 */
export function useMenuGate(menuKey: string, fallbackLabel = ''): MenuGate {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const row = snap.rows?.find((m) => m.menuKey === menuKey);
  if (!row) return { enabled: true, label: fallbackLabel };
  return { enabled: row.visible, label: row.label || fallbackLabel };
}

/**
 * 여러 메뉴를 한 번에 판정하는 함수 (2026-09-09 · 판 5 · 빠른 실행).
 *
 * ★ 훅은 목록 안에서 못 부른다 — 바로가기 열둘을 거르려고 `useMenuGate` 를 열두 번 부를 수는
 *   없다. 판정 규칙은 위와 **똑같다**(행 없음 · 레지스트리 없음 → fail-open).
 */
export function useMenuEnabledFn(): (menuKey: string) => boolean {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return (menuKey: string) => {
    const row = snap.rows?.find((m) => m.menuKey === menuKey);
    return row ? row.visible : true;
  };
}

/** 테스트 전용 — 싱글턴 주입(실 요청 없이 상태 고정). */
export function _setMenuGateForTests(rows: Array<Partial<MenuVisibilityRow>> | null): void {
  started = true;
  setState({
    rows: rows
      ? rows.map((r) => ({
        menuKey: r.menuKey ?? '', label: r.label ?? '', moduleKey: r.moduleKey ?? null,
        section: r.section ?? 'primary', sortOrder: r.sortOrder ?? 0, visible: r.visible ?? true,
      }))
      : null,
  });
}
